/**
 * features/hr/LeaveCardPrint.tsx
 * ------------------------------------------------------------------
 * HR LEAVE — "Print leave card": the yearly card in the layout HR keeps
 * on paper (lib/leaveCard + lib/leaveCardPdf), for one person (the staff
 * profile's Leave balances) or many at once (the Leave balances board —
 * teaching staff, or everyone), a page each in one PDF.
 *
 * Every figure comes through lib/leavePolicy leaveBasis — the same
 * entitlement and anchor as the leave screens — so the card can never
 * disagree with them. "Approved by" and "Designation" are asked for, with
 * the last values used on this computer as the default (Santhosh Varma,
 * Accountant, as on the 2026 cards); "Prepared by" is whoever prints.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { Printer, Loader2, X } from 'lucide-react';
import { btnPrimary, btnGhost, glassFieldClass } from '@/features/finance/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useHrData } from '@/features/hr/useHrData';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { leaveBasis, currentLeaveYear } from '@/lib/leavePolicy';
import { DEFAULT_LEAVE_ACCRUAL_START } from '@/lib/hrPayroll';
import type { HrLeavePolicy } from '@/lib/leavePolicy';
import { buildLeaveCard } from '@/lib/leaveCard';
import { buildLeaveCardsPdf, type LeaveCardPage } from '@/lib/leaveCardPdf';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import type { HrStaff } from '@/lib/hr';

const SIGN_KEY = 'lkc.leaveCard.sign';
function savedSign(): { approvedBy: string; designation: string } {
  try {
    const v = JSON.parse(localStorage.getItem(SIGN_KEY) ?? 'null');
    if (v && typeof v.approvedBy === 'string') return v;
  } catch { /* storage unavailable */ }
  return { approvedBy: 'Santhosh Varma', designation: 'Accountant' };
}

type HrData = ReturnType<typeof useHrData>;

/** One person's card, worked out the way the leave screens work it out. */
export function leaveCardPage(staff: HrStaff, data: HrData, policies: HrLeavePolicy[], asOf = new Date()): LeaveCardPage {
  const year = currentLeaveYear(asOf);
  const allocation = data.leaveAllocations.find((a) => a.staffId === staff.id);
  const basis = leaveBasis({
    category: staff.category, allocation, policies, year,
    accrualStart: data.payrollSettings?.leaveAccrualStart, employmentStart: staff.startDate,
  });
  const card = buildLeaveCard({
    year: Number(year),
    entitlement: basis.allocation.entitlements.annual ?? 0,
    opening: Number(allocation?.openingBalances?.annual) || 0,
    anchor: (basis.opts.accrualStart ?? DEFAULT_LEAVE_ACCRUAL_START).slice(0, 10),
    employmentStart: staff.startDate,
    header: allocation?.leaveCard2026,
    leave: data.leave.filter((l) => l.staffId === staff.id),
    asOf,
  });
  return { name: `${staff.firstName ?? ''} ${staff.lastName ?? ''}`.trim(), card };
}

export default function LeaveCardPrint({ staff, label = 'Print leave card', compact = false }: {
  /** Who to print, in order. */
  staff: HrStaff[];
  label?: string;
  compact?: boolean;
}) {
  const data = useHrData();
  const { policies } = useLeavePolicies();
  const { userData } = useAuth();
  const [open, setOpen] = useState(false);
  const [sign, setSign] = useState(savedSign);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function print() {
    setBusy(true);
    setErr('');
    try {
      try { localStorage.setItem(SIGN_KEY, JSON.stringify(sign)); } catch { /* storage unavailable */ }
      const pages = staff.map((s) => leaveCardPage(s, data, policies));
      const logo = await loadLogoDataUrl().catch(() => null);
      const pdf = buildLeaveCardsPdf(pages, { ...sign, preparedBy: userData?.displayName || userData?.email || '' }, logo);
      const name = staff.length === 1 ? `Leave card ${pages[0].name} ${pages[0].card.year}` : `Leave cards ${pages[0]?.card.year ?? ''}`;
      pdf.save(`${name}.pdf`);
      setOpen(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!staff.length) return null;
  return (
    <span className="relative inline-block">
      <button
        onClick={() => setOpen((v) => !v)}
        className={compact
          ? 'flex items-center gap-1 text-xs font-medium text-[var(--accent-link)] hover:underline'
          : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'}
      >
        <Printer size={compact ? 12 : 14} /> {label}{staff.length > 1 ? ` (${staff.length})` : ''}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg p-3 space-y-2 text-left">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Signatures on the card</p>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
          </div>
          <label className="block text-xs text-gray-500">Approved by
            <input value={sign.approvedBy} onChange={(e) => setSign({ ...sign, approvedBy: e.target.value })} className={glassFieldClass('py-1.5 text-sm')} />
          </label>
          <label className="block text-xs text-gray-500">Designation
            <input value={sign.designation} onChange={(e) => setSign({ ...sign, designation: e.target.value })} className={glassFieldClass('py-1.5 text-sm')} />
          </label>
          <p className="text-[11px] text-gray-400">Prepared by: {userData?.displayName || userData?.email}</p>
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex gap-2 pt-1">
            <button className={btnPrimary} disabled={busy || data.loading} onClick={print}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} Print
            </button>
            <button className={btnGhost} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </span>
  );
}
